/* eslint-disable @tencent/i18next/no-literal-string */
import _set from 'lodash/set';
import { PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import {
    BaseChartPresetHandler,
    ModifyPlotAreaProps,
    SeriesInitialProps,
    SeriesPresetPoint,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import {
    IChartAxis,
    IChartPlotArea,
    IChartSpace,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { IBaseSubChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/base-sub-chart.interface';
import {
    STCrossBetween,
    STGrouping,
    SubChartType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { AreaChart } from '../../sub-chart/area-chart';
import { IAreaChart } from '../../../Interface/chart-v3/sub-chart';
import { AreaChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2/area-chart-series';

// office的时间戳，对应 2002/1/5, 2002/1/6, 2002/1/7, 2002/1/8, 2002/1/9
export const AREA_CHART_SERIES_CATEGORIES_PRESET = ['37261', '37262', '37263', '37264', '37265'];
export const AREA_CHART_VALUES_PRESET = [
    [32, 32, 28, 12, 15],
    [12, 12, 12, 21, 28],
];

export class AreaChartPresetHandler extends BaseChartPresetHandler {
    protected defaultChartDataPreset = {
        categories: AREA_CHART_SERIES_CATEGORIES_PRESET,
        values: AREA_CHART_VALUES_PRESET,
    };

    get ChartSeries() {
        return AreaChartSeries;
    }

    get SubChart() {
        return AreaChart;
    }

    public createChartModel(
        type: PresetChartType,
        chartSpace: IChartSpace,
        initialProps?: SeriesInitialProps,
    ): IAreaChart {
        const subChart = super.createChartModel(type, chartSpace, initialProps);

        switch (type) {
            case PresetChartType.AREA:
                this.setAreaStandard(subChart);
                break;
            case PresetChartType.AREA_STACKED:
                this.setAreaStacked(subChart);
                break;
            case PresetChartType.AREA_PERCENT_STACKED:
                this.setAreaPercentStacked(subChart);
                break;
        }

        return subChart;
    }

    public modifyPlotArea(param: {
        plotArea: IChartPlotArea;
        newSubChart: IBaseSubChart;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
    }): ModifyPlotAreaProps {
        const baseProps = super.modifyPlotArea(param);

        return {
            removeProps: baseProps.removeProps,
            updateProps: {
                ...baseProps.updateProps,
                valueAxes: [
                    {
                        ...baseProps.updateProps?.valueAxes?.[0],
                        crossBetween: STCrossBetween.midCat,
                    },
                ],
            },
        };
    }

    public generateAxesPreset(
        subChart: IBaseSubChart,
        type: PresetChartType,
    ): { categoryAxes?: IChartAxis[]; valueAxes?: IChartAxis[]; dateAxes?: IChartAxis[] } {
        const baseAxes = super.generateAxesPreset(subChart, type);
        const grouping = 'grouping' in subChart ? (subChart.grouping as STGrouping) : undefined;

        const { axisIds } = subChart;
        const [categoryAxisId, valueAxisId] = axisIds || [];
        const valueAxes = [
            this.createValueAxis(
                {
                    axId: valueAxisId,
                    crossAx: categoryAxisId,
                    // 如果要X坐标轴顶住Y轴的话，需要设置Y轴的crossBetween为midcat
                    crossBetween: STCrossBetween.midCat,
                },
                type,
                grouping,
            ),
        ];

        return {
            ...baseAxes,
            valueAxes,
        };
    }

    protected getStyle(newSer, oldSer, newSubChart: IBaseSubChart, oldSubChart: IBaseSubChart) {
        const style = super.getStyle(newSer, oldSer, newSubChart, oldSubChart);
        if (oldSubChart.type === SubChartType.LineChart) {
            delete style.border;
        }
        return style;
    }

    protected shouldExtendTrendline(targetType: PresetChartType) {
        return targetType === PresetChartType.AREA;
    }

    protected generatePresetSeriesInitialProps(
        seriesIndex: number,
        points: Array<SeriesPresetPoint>,
        seriesName?: string,
        showDataLabels = false,
    ) {
        const baseProps = super.generatePresetSeriesInitialProps(seriesIndex, points, seriesName, showDataLabels);
        return {
            ...baseProps,
            categoryAxisData: {
                formatCode: 'm/d/yy',
                type: 'nr',
            },
        };
    }

    protected getValueAxisCrossBetween(targetType: PresetChartType): STCrossBetween {
        return STCrossBetween.midCat;
    }

    private setAreaStandard(subChart: IAreaChart) {
        subChart.grouping = STGrouping.standard;
    }

    private setAreaStacked(subChart: IAreaChart) {
        subChart.grouping = STGrouping.stacked;
    }

    private setAreaPercentStacked(subChart: IAreaChart) {
        subChart.grouping = STGrouping.percentStacked;
    }
}
